import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { campaignApi } from '../../utils/api';
import type { CampaignBannerData } from '../../types';

/**
 * Promotional campaign banner displayed at the top of the page.
 * Reads from the active-banner API endpoint (Redis-cached, 5-min TTL).
 * Dismissible per session via local state.
 */
const CampaignBanner: React.FC = () => {
  const { t } = useTranslation();
  const [banner, setBanner] = useState<CampaignBannerData | null>(null);
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    const fetchBanner = async () => {
      const response = await campaignApi.getActiveBanner();
      if (response.success && response.data) {
        setBanner(response.data as CampaignBannerData);
      }
    };
    fetchBanner();
  }, []);

  // Validate CTA URL to prevent XSS via javascript: or data: URIs
  const isSafeUrl = (url: string | undefined): boolean => {
    if (!url) return false;
    try {
      const parsed = new URL(url, window.location.origin);
      return parsed.protocol === 'https:' || parsed.protocol === 'http:';
    } catch {
      return false;
    }
  };

  if (!banner || isDismissed) return null;

  const safeCtaUrl = isSafeUrl(banner.ctaUrl) ? banner.ctaUrl : undefined;

  return (
    <div
      className="bg-gradient-to-r from-primary-500 to-primary-600 text-white"
      role="complementary"
      aria-label={t('campaign.learnMore')}
    >
      <div className="container-app py-3 flex items-center justify-between gap-4">
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm sm:text-base truncate">
            {banner.headline}
          </p>
          <p className="text-primary-100 text-xs sm:text-sm truncate">
            {banner.eventName} &middot; {banner.eventDates}
          </p>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {safeCtaUrl && (
          <a
            href={safeCtaUrl}
            rel="noopener noreferrer"
            className="bg-white text-primary-600 font-semibold text-sm px-4 py-1.5 rounded-lg hover:bg-primary-50 transition-colors"
          >
            {banner.ctaLabel}
          </a>
          )}
          <button
            onClick={() => setIsDismissed(true)}
            className="text-primary-200 hover:text-white p-1 transition-colors"
            aria-label={t('campaign.dismiss')}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default CampaignBanner;
